import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { any, compose, identity } from 'ramda';
import { dotPathSatisfies, isNotEmpty, isTruthy } from 'ramda-extension';

import { logger } from '#src/lib/logger';
import {
  useCompleteOrder,
  useCreateNotification,
  useOrderQuery,
  usePathnameChanged,
} from '#src/hooks';
import { selectTimeZone } from '#src/store/intl/selectors';
import { selectParams } from '#src/store/location/selectors';
import { getEnvironment } from '#src/store/environment/selectors';
import {
  selectMembershipReturnGame,
  selectAuthOrProspectUser,
} from '#src/store/session/selectors';
import { setMembershipReturnGame } from '#src/store/session/actions';
import Loading from '#src/components/Loading';
import ErrorDisplay from '#src/components/ErrorDisplay';

import { PaymentError } from './PaymentError/PaymentError.js';
import {
  selectCartComponent,
  selectGameOrderItem,
  selectMembershipOrderItem,
  useBraintreeState,
  useBraintreeToken,
  useDisabledState,
  useMembershipPlanContent,
  useRemoveItem,
  useResumeOrderMutation,
  useSelectPaymentPlan,
} from './helpers';
import { useOrderAnalyticsContext } from './helpers/use-order-analytics-context.js';
import { useHashedEmailDataLayer } from './helpers/use-hashed-email-datalayer';
import { useRecreateOrderOnAuthError } from './helpers/user-recreate-order-on-auth-error';

// TODO: this is a temporary fallback until DietBet returns `offerTerms` for
// membership offers.
const DEFAULT_MEMBERSHIP_LEGAL_TEXT = `
<p>By clicking on the "Pay" button, I agree to the following: I am at least
18 years old and agree to the WayBetter&nbsp;<a href="/terms">
Terms of Service</a>&nbsp;and&nbsp;<a href="/privacy">Privacy Policy</a>. I authorize WayBetter to charge my card $50 for the first year of my
subscription and $50 yearly (plus tax, where applicable). No refunds for partial
years. To cancel, simply email&nbsp;<a href="http://mailto:support@waybetter.app">support@waybetter.app</a>.</p>
`;

function usePaymentMethodReady(initialState, payload) {
  const [paymentMethodReady, setPaymentMethodReady] = useState(initialState);
  const onPaymentMethodReadyChange = (isReady, event) => {
    if (isReady && !paymentMethodReady) {
      analytics.track('Payment Method Ready', {
        ...payload,
        type: event?.type,
      });
    }
    setPaymentMethodReady(isReady);
  };

  return [paymentMethodReady, onPaymentMethodReadyChange];
}

const Checkout = ({
  theme,
  joinCtaLabel,
  joinWithPaymentCtaLabel,
  freeTrialCta,
  ...props
}) => {
  const dispatch = useDispatch();
  const timeZone = useSelector(selectTimeZone);
  const membershipReturnGame = useSelector(selectMembershipReturnGame);
  const { orderId, cartVariant } = useSelector(selectParams);
  const { SUPPORT_EMAIL } = useSelector(getEnvironment);
  const createNotification = useCreateNotification();
  const isPathnameChanged = usePathnameChanged();

  useOrderAnalyticsContext(orderId);

  // The cart needs to know if the user is a member, to determine what cart to show.
  // Since this requires a call to the backend, we can leverage this to also get the
  // latest values of the user's firstName and email.
  // As such, the component only grabs the id and role from the client-side-stored
  // version of the user.
  // The server user (set to the "user" variable by the userMembershipPlanContent)
  // will be preferred for all other usages.
  const { id: userId, role } = useSelector(selectAuthOrProspectUser) || {};
  const requiresRegistration = role == 'prospect';

  const {
    user,
    error: errorFetchingMembershipPlanContent,
    loading: loadingMembershipPlanContent,
  } = useMembershipPlanContent();

  useHashedEmailDataLayer(user?.email, 'hashedEmail');

  const {
    bets,
    discounts,
    error: errorFetchingOrder,
    games,
    guaranteeDays,
    hasFreeTrial,
    hasPromoCodes,
    isAdHocMembershipOrder,
    loading: loadingOrder,
    memberships,
    order,
    refreshOrder,
  } = useOrderQuery({ orderId });
  const { loading: resumingOrder, error: errorResumingOrder } =
    useResumeOrderMutation({ order, orderId, userId });
  const { requiresPaymentMethod, requiresStoreInVault, orderItems, amountDue } =
    order;
  const [paymentError, setPaymentError] = useState(null);
  const { completeOrder } = useCompleteOrder({ orderId, timeZone });
  const [paymentMethodReady, setPaymentMethodReady] = usePaymentMethodReady(
    false,
    { orderId }
  );
  const { disabled } = useDisabledState({
    orderStatus: order.status,
    paymentMethodReady,
    requiresPaymentMethod,
  });
  const onPromoError = (error) =>
    createNotification({ message: error.message, status: 'error' });

  const {
    token,
    loading: loadingBraintreeToken,
    error: errorFetchingToken,
  } = useBraintreeToken();

  const { selectPaymentPlan } = useSelectPaymentPlan({
    orderId,
    orderItems,
    amountDue,
    requiresPaymentMethod,
    requiresStoreInVault,
  });
  const { removeItem } = useRemoveItem({ orderId });
  const { braintree, setBraintreeInstance } = useBraintreeState({
    onInitialPaymentMethodRequestable: () => setPaymentMethodReady(true),
  });

  useEffect(() => {
    if (paymentError) {
      const braintreeCode = paymentError._braintreeWebError?.code;
      analytics.track('Payment Error', { braintreeCode, orderId });
    }
  }, [paymentError, orderId]);

  const gameOrderItem = selectGameOrderItem(order);
  const membershipOrderItem = selectMembershipOrderItem(order);

  const errors = [
    errorFetchingMembershipPlanContent,
    errorFetchingOrder,
    errorFetchingToken,
    errorResumingOrder,
  ].filter(identity);

  useRecreateOrderOnAuthError({ errors, orderId });

  const showLoading =
    loadingMembershipPlanContent ||
    loadingOrder ||
    loadingBraintreeToken ||
    resumingOrder ||
    isPathnameChanged;
  if (showLoading) return <Loading />;

  if (errors.length) {
    errors.forEach((e) => {
      logger.alarm(e, { orderId }, 'User experiencing error during checkout.');
    });
    return <ErrorDisplay theme={theme} errors={errors} />;
  }

  if (paymentError) {
    return (
      <PaymentError
        error={paymentError}
        onDismiss={() => setPaymentError(null)}
      />
    );
  }

  const membershipDisclaimer =
    memberships?.[0]?.content?.disclosure || DEFAULT_MEMBERSHIP_LEGAL_TEXT;
  const membershipCta = hasFreeTrial ? freeTrialCta : 'Pay';
  const gameCta = requiresPaymentMethod
    ? joinWithPaymentCtaLabel
    : joinCtaLabel;
  const hasMembershipInCart = isNotEmpty(memberships);
  const ctaLabel = hasMembershipInCart ? membershipCta : gameCta;
  const requiresMembershipInCart = any(
    dotPathSatisfies(isTruthy, 'game.isMembersOnlyGame'),
    games || []
  );

  const extendedProps = {
    ...props,
    bets,
    braintree,
    completeOrder,
    createNotification,
    ctaLabel,
    disabled,
    discounts,
    dispatch,
    games,
    guaranteeDays,
    hasFreeTrial,
    hasMembershipInCart,
    hasPromoCodes,
    isAdHocMembershipOrder,
    membershipDisclaimer,
    membershipReturnGame,
    memberships,
    onPromoError,
    order,
    orderId,
    referringGameId: membershipReturnGame.id,
    refreshOrder,
    removeItem,
    requiresMembershipInCart,
    requiresRegistration,
    requiresStoreInVault: order.requiresStoreInVault,
    selectPaymentPlan,
    setBraintreeInstance,
    setMembershipReturnGame: compose(dispatch, setMembershipReturnGame),
    setPaymentMethodReady,
    setPaymentError: (er) => {
      braintree.clearSelectedPaymentMethod();
      setPaymentError(er);
    },
    SUPPORT_EMAIL,
    theme,
    token,
    user,
  };

  const CartComponent = selectCartComponent({
    gameOrderItem,
    membershipOrderItem,
    theme,
    user,
    cartVariant,
  });

  return <CartComponent {...extendedProps} />;
};

Checkout.displayName = 'CheckoutEnhanced';

export default Checkout;
